import { LocalizedLink } from "@/i18n/language";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import BookingCompanyNotice from "@/components/feature/BookingCompanyNotice";
import GalleryGrid from "@/components/feature/GalleryGrid";
import {
  FloorPlanTabs,
  FloorPlanImage,
} from "@/components/feature/VillaFloorPlan";
import { img } from "@/lib/images";
import { usePriceMap } from "@/hooks/usePrices";
import { useGalleryCaptions } from "@/hooks/useGalleryCaptions";
import { bougainvilleaGalleryImages } from "./gallery-data";

export default function VillaBougainvilleaPage() {
  const { i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const { priceMap, money } = usePriceMap();
  const galleryImages = useGalleryCaptions("villa-bougainvillea", bougainvilleaGalleryImages);

  const [planOpen, setPlanOpen] = useState(false);

  const roomFeatures = isDe
    ? [
        "Klimaanlage & Ventilator im Zimmer",
        "großer Kleiderschrank",
        "Leselampen am Bett",
        "Moskitonetz",
        "kein TV im Zimmer",
        "täglich 2 Flaschen Mineralwasser kostenlos",
      ]
    : [
        "Air conditioning & ceiling fan",
        "Large wardrobe",
        "Reading lamps at the bed",
        "Mosquito net",
        "No TV in the room",
        "Daily 2 bottles of mineral water free",
      ];

  const bathFeatures = isDe
    ? [
        "großes Badezimmer mit WC & Dusche",
        "Handtücher, Toilettenartikel (Duschgel, Shampoo, Conditioner)",
        "Haarföhn",
      ]
    : [
        "Large bathroom with WC & shower",
        "Towels, toiletries (shower gel, shampoo, conditioner)",
        "Hair dryer",
      ];

  const terraceFeatures = isDe
    ? [
        "Große Rattanmöbel-Sitzecke",
        "Anrichte mit Kühlschrank",
        "Audioanlage für CD, DVD, USB, iPhone",
      ]
    : [
        "Large rattan seating area",
        "Sideboard with refrigerator",
        "Audio system for CD, DVD, USB, iPhone",
      ];

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={isDe ? "Villa Bougainvillea" : "Villa Bougainvillea"}
          title={isDe ? "Ein Schlafzimmer" : "One Bedroom"}
          eyebrowSize="text-lg md:text-2xl"
          titleSize="text-lg md:text-xl"
          subtitle={
            isDe
              ? "Alle Details unserer kleinsten Villa"
              : "All details of our smallest villa"
          }
          image={img("villas/villa-bougainvillea/header-villabougainvillea-01.jpg")}
          imageAlt="Villa Bougainvillea"
        />

        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left column */}
            <div className="lg:col-span-2">
              {/* Gallery */}
              <div className="mb-8">
                <GalleryGrid images={galleryImages} />
              </div>

              {/* Parameters */}
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="flex items-center gap-2 bg-background-100 px-4 py-2 rounded-full text-sm text-foreground-700">
                  <i className="ri-user-line text-accent-700"></i>
                  {isDe ? "Max 2 Personen" : "Max 2 persons"}
                </div>
                <div className="flex items-center gap-2 bg-background-100 px-4 py-2 rounded-full text-sm text-foreground-700">
                  <i className="ri-hotel-bed-line text-accent-700"></i>
                  {isDe ? "1 King Size Bett" : "1 King Size bed"}
                </div>
                <div className="flex items-center gap-2 bg-background-100 px-4 py-2 rounded-full text-sm text-foreground-700">
                  <i className="ri-ruler-line text-accent-700"></i>
                  {isDe ? "78 qm" : "78 sqm"}
                </div>
                <FloorPlanTabs
                  open={planOpen}
                  onToggle={() => setPlanOpen((v) => !v)}
                />
              </div>

              {/* Floor plan */}
              {planOpen && (
              <FloorPlanImage
                deImg={img("villas/villa-bougainvillea/floor%20plan%20villa%20Bougainvillea_ger.png")}
                enImg={img("villas/villa-bougainvillea/floor%20plan%20villa%20bougainvillea_eng.png")}
                alt={isDe ? "Grundriss Villa Bougainvillea" : "Floor plan Villa Bougainvillea"}
              />
              )}

              {/* Description */}
              <p className="text-foreground-700 leading-relaxed mb-8 text-base">
                {isDe
                  ? "Die Villa verfügt über ein sehr geräumiges Zimmer und ein großes Badezimmer, sowie eine große Terrasse. Es stehen alle Einrichtungen wie Garten, Pool und Speisepavillon zur Verfügung."
                  : "The villa features a very spacious room and a large bathroom, as well as a large terrace. All facilities such as garden, pool and dining pavilion are available."}
              </p>

              {/* Features */}
              <div className="space-y-8">
                <div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-star-line text-accent-700"></i>
                    {isDe ? "ZIMMER" : "ROOM"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {roomFeatures.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground-700"
                      >
                        <i className="ri-check-line text-accent-700 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-drop-line text-accent-700"></i>
                    {isDe ? "BADEZIMMER" : "BATHROOM"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {bathFeatures.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground-700"
                      >
                        <i className="ri-check-line text-accent-700 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-sun-line text-accent-700"></i>
                    {isDe ? "TERRASSE" : "TERRACE"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {terraceFeatures.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground-700"
                      >
                        <i className="ri-check-line text-accent-700 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Price box */}
                <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                  <div className="text-xs text-foreground-500 uppercase tracking-wider mb-1">
                    {isDe ? "Preis" : "Price"}
                  </div>
                  <div className="text-3xl font-heading text-foreground-950 mb-1">
                    {money(priceMap["villa_bougainvillea_1"])}
                  </div>
                  <div className="text-sm text-foreground-600 mb-6">
                    {isDe ? "pro Zimmer / Nacht · inklusive Frühstück" : "per room / night · incl. breakfast"}
                  </div>
                  <LocalizedLink
                    to="/villa-reservation?villa=villa-bougainvillea"
                    className="block w-full text-center bg-accent-500 hover:bg-accent-100 text-background-50 hover:text-foreground-950 py-3 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className="ri-calendar-check-line mr-2"></i>
                    {isDe ? "Jetzt buchen" : "Book now"}
                  </LocalizedLink>
                  <BookingCompanyNotice isDe={isDe} />
                </div>

                {/* Other villas */}
                <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                  <h3 className="font-heading text-lg text-foreground-950 mb-4">
                    {isDe ? "Weitere Villen" : "More Villas"}
                  </h3>
                  <div className="space-y-4">
                    <LocalizedLink
                      to="/villa-frangipani"
                      className="flex items-center gap-3 group"
                    >
                      <img
                        src={img("villas/villa-frangipani/villa-frangipani-view-01.jpg")}
                        alt="Villa Frangipani"
                        className="w-14 h-14 rounded object-cover flex-shrink-0"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                          Villa Frangipani
                        </div>
                        <div className="text-xs text-foreground-500">
                          {isDe ? "2 Zimmer · 110 qm" : "2 rooms · 110 sqm"}
                        </div>
                      </div>
                    </LocalizedLink>
                    <LocalizedLink
                      to="/villa-hibiscus"
                      className="flex items-center gap-3 group"
                    >
                      <img
                        src={img("villas/villa-hibiscus/villa-hibiscus-view-01.jpg")}
                        alt={isDe ? "Villa Hibiskus" : "Villa Hibiscus"}
                        className="w-14 h-14 rounded object-cover flex-shrink-0"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                          {isDe ? "Villa Hibiskus" : "Villa Hibiscus"}
                        </div>
                        <div className="text-xs text-foreground-500">
                          {isDe ? "3 Zimmer · 230 qm" : "3 rooms · 230 sqm"}
                        </div>
                      </div>
                    </LocalizedLink>
                  </div>
                </div>

                {/* Contact */}
                <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                  <h3 className="font-heading text-lg text-foreground-950 mb-4">
                    {isDe ? "Kontakt" : "Contact"}
                  </h3>
                  <div className="space-y-3 text-sm text-foreground-700">
                    <div className="flex items-center gap-2">
                      <i className="ri-phone-line text-accent-700"></i>
                      <span>+62 81 246 21 8000</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-mail-line text-accent-700"></i>
                      <span>office@balivwtour.com</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}